export const LIGHT_THEME_CSS = `
  [data-theme="light"], :root {
    --paper: #FFFFFF;
    --paper-2: #F5F6F8;
    --paper-3: #EDEEF1;
    --card: #FFFFFF;
    --card-2: #F5F6F8;
    --line: #E6E7EB;
    --line-strong: #D5D7DE;
    --ink: #1D1E27;
    --ink-2: #5A5B68;
    --ink-3: #8E8F9C;
    --ink-4: #B8B9C8;
    --ink-inv: #FFFFFF;
    --accent: #2D5CF6;
    --accent-2: #1A46D9;
    --accent-soft: #EEF2FF;
    --accent-ink: #2D5CF6;
    --accent-gradient: linear-gradient(135deg, #4B6EF7 0%, #7C3AED 100%);
    --success: #1F8A5B;
    --success-soft: #E8F6EF;
    --success-line: #B3DEC9;
    --warn: #B7791F;
    --warn-soft: #FEF3E2;
    --warn-line: #F6D88E;
    --danger: #C2452E;
    --danger-soft: #FDECEA;
    --danger-line: #F0B9B2;
    --info: #1C71B8;
    --info-soft: #E8F3FC;
    --info-line: #A8CFF0;
    --neutral-soft: #F1F2F4;
    --overlay: rgba(29,30,39,0.5);
    --shadow-sm: 0 1px 3px rgba(29,30,39,0.08);
    --shadow: 0 2px 6px rgba(29,30,39,0.09);
    --shadow-md: 0 4px 12px rgba(29,30,39,0.10);
    --shadow-lg: 0 8px 24px rgba(29,30,39,0.12);
    --shadow-focus: 0 0 0 3px rgba(45,92,246,0.18);
    --r-1: 3px;
    --r-2: 6px;
    --r-3: 9px;
    --r-4: 14px;
    --r-full: 9999px;
    --fs-xs: 11px;
    --fs-sm: 12px;
    --fs-base: 13px;
    --fs-md: 14px;
    --fs-lg: 16px;
    --fs-xl: 18px;
    --fs-2xl: 22px;
    --fs-3xl: 28px;
    --fw-regular: 400;
    --fw-medium: 500;
    --fw-semibold: 600;
    --font-sans: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif;
    --font-mono: 'IBM Plex Mono', 'Fira Mono', monospace;
    --nav-w: 248px;
    --topbar-h: 56px;
    --panel-w: 380px;
    --t-fast: 100ms;
    --t-base: 160ms;
    --t-slow: 260ms;
    --ease-out: cubic-bezier(0.2,0,0,1);
  }
`;

export const DARK_THEME_CSS = `
  [data-theme="dark"] {
    --paper: #1A1B23;
    --paper-2: #14151C;
    --paper-3: #0F1016;
    --card: #272838;
    --card-2: #2F3042;
    --line: #383952;
    --line-strong: #44466A;
    --ink: #F5F4F0;
    --ink-2: #B0B1C4;
    --ink-3: #6E6F84;
    --ink-4: #44455A;
    --ink-inv: #1D1E27;
    --accent: #7B9FFF;
    --accent-2: #9BB5FF;
    --accent-soft: #1E2845;
    --accent-ink: #7B9FFF;
    --accent-gradient: linear-gradient(135deg, #5C7CFF 0%, #A063F5 100%);
    --success: #3DBD82;
    --success-soft: #0E2E1E;
    --success-line: #1A5C38;
    --warn: #F0A830;
    --warn-soft: #2E1F06;
    --warn-line: #5C3D0A;
    --danger: #F4694F;
    --danger-soft: #2E0F0A;
    --danger-line: #5C1F14;
    --info: #5BAEE8;
    --info-soft: #0C1E30;
    --info-line: #163C5C;
    --neutral-soft: #24253A;
    --overlay: rgba(0,0,0,0.6);
    --shadow-sm: 0 1px 3px rgba(0,0,0,0.3);
    --shadow: 0 2px 6px rgba(0,0,0,0.35);
    --shadow-md: 0 4px 12px rgba(0,0,0,0.4);
    --shadow-lg: 0 8px 24px rgba(0,0,0,0.5);
    --shadow-focus: 0 0 0 3px rgba(123,159,255,0.25);
  }
`;

export const BASE_CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; }
  body { background: var(--paper); color: var(--ink); font-family: var(--font-sans); font-size: var(--fs-base); line-height: 1.5; -webkit-font-smoothing: antialiased; }

  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--line-strong); border-radius: var(--r-full); }

  /* Buttons */
  .t-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 0 14px; height: 34px; border-radius: var(--r-2); font-size: var(--fs-base); font-weight: var(--fw-medium); font-family: var(--font-sans); cursor: pointer; border: 1.5px solid transparent; transition: background var(--t-fast) var(--ease-out), color var(--t-fast), border-color var(--t-fast), opacity var(--t-fast); text-decoration: none; white-space: nowrap; }
  .t-btn:disabled { opacity: 0.45; cursor: not-allowed; pointer-events: none; }
  .t-btn--accent { background: var(--accent); color: #fff; }
  .t-btn--accent:hover:not(:disabled) { background: var(--accent-2); }
  .t-btn--ghost { background: transparent; color: var(--ink-2); }
  .t-btn--ghost:hover:not(:disabled) { background: var(--neutral-soft); color: var(--ink); }
  .t-btn--outline { background: transparent; color: var(--ink); border-color: var(--line-strong); }
  .t-btn--outline:hover:not(:disabled) { background: var(--neutral-soft); }
  .t-btn--danger { background: var(--danger-soft); color: var(--danger); border-color: var(--danger-line); }
  .t-btn--danger:hover:not(:disabled) { background: var(--danger); color: #fff; }
  .t-btn--xs { height: 24px; padding: 0 8px; font-size: var(--fs-xs); border-radius: var(--r-1); gap: 4px; }
  .t-btn--sm { height: 28px; padding: 0 10px; font-size: var(--fs-sm); }
  .t-btn--lg { height: 40px; padding: 0 20px; font-size: var(--fs-md); }
  .t-btn--block { width: 100%; }
  .t-btn--icon { padding: 0; width: 34px; }
  .t-btn--icon.t-btn--xs { width: 24px; }
  .t-btn--icon.t-btn--sm { width: 28px; }
  .t-btn--icon.t-btn--lg { width: 40px; }

  /* Inputs */
  .t-input { display: block; width: 100%; height: 34px; padding: 0 10px; border-radius: var(--r-2); border: 1.5px solid var(--line-strong); background: var(--card); color: var(--ink); font-size: var(--fs-base); font-family: var(--font-sans); outline: none; transition: border-color var(--t-fast); }
  .t-input:focus { border-color: var(--accent); box-shadow: var(--shadow-focus); }
  .t-input--sm { height: 28px; font-size: var(--fs-sm); }
  textarea.t-input { height: auto; padding: 8px 10px; resize: vertical; }
  select.t-input { appearance: none; cursor: pointer; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238E8F9C' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 10px center; padding-right: 30px; }
  .t-field { display: flex; flex-direction: column; gap: 5px; }
  .t-field label { font-size: var(--fs-sm); font-weight: var(--fw-medium); color: var(--ink-2); }
  .t-field-hint { font-size: var(--fs-xs); color: var(--ink-3); }
  .t-field-error { font-size: var(--fs-xs); color: var(--danger); }

  /* Card */
  .t-card { background: var(--card); border-radius: var(--r-3); border: 1px solid var(--line); }
  .t-card-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid var(--line); gap: 10px; }
  .t-card-head h3 { font-size: var(--fs-md); font-weight: var(--fw-semibold); }
  .t-card-sub { font-size: var(--fs-sm); color: var(--ink-3); margin-top: 2px; }
  .t-card-body { padding: 16px; }
  .t-card-foot { padding: 12px 16px; border-top: 1px solid var(--line); background: var(--paper); border-radius: 0 0 var(--r-3) var(--r-3); }

  /* Chip */
  .t-chip { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: var(--r-full); font-size: var(--fs-xs); font-weight: var(--fw-medium); border: 1px solid transparent; white-space: nowrap; }
  .t-chip--neutral { background: var(--neutral-soft); color: var(--ink-2); border-color: var(--line); }
  .t-chip--accent { background: var(--accent-soft); color: var(--accent-ink); border-color: rgba(45,92,246,0.2); }
  .t-chip--success { background: var(--success-soft); color: var(--success); border-color: var(--success-line); }
  .t-chip--warn { background: var(--warn-soft); color: var(--warn); border-color: var(--warn-line); }
  .t-chip--danger { background: var(--danger-soft); color: var(--danger); border-color: var(--danger-line); }
  .t-chip--info { background: var(--info-soft); color: var(--info); border-color: var(--info-line); }
  .t-chip-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }

  /* Avatar */
  .t-avatar { display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; background: var(--neutral-soft); color: var(--ink-2); font-weight: var(--fw-semibold); font-size: var(--fs-sm); border: 1.5px solid var(--line); overflow: hidden; flex-shrink: 0; }
  .t-avatar--xs { width: 22px; height: 22px; font-size: 9px; }
  .t-avatar--sm { width: 28px; height: 28px; font-size: var(--fs-xs); }
  .t-avatar { width: 34px; height: 34px; }
  .t-avatar--md { width: 34px; height: 34px; }
  .t-avatar--lg { width: 44px; height: 44px; font-size: var(--fs-md); }

  /* Tabs */
  .t-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--line); padding: 0 2px; }
  .t-tab { display: inline-flex; align-items: center; gap: 5px; padding: 8px 10px; border: none; background: transparent; color: var(--ink-3); font-size: var(--fs-sm); font-weight: var(--fw-medium); font-family: var(--font-sans); cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color var(--t-fast), border-color var(--t-fast); }
  .t-tab:hover { color: var(--ink); }
  .t-tab.is-active { color: var(--accent); border-bottom-color: var(--accent); }
  .t-tab-count { display: inline-flex; align-items: center; justify-content: center; min-width: 17px; height: 17px; padding: 0 4px; border-radius: var(--r-full); background: var(--neutral-soft); font-size: 10px; font-weight: var(--fw-semibold); color: var(--ink-3); }
  .t-tab.is-active .t-tab-count { background: var(--accent-soft); color: var(--accent-ink); }

  /* Modal */
  .t-modal-backdrop { position: fixed; inset: 0; background: var(--overlay); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; animation: t-fadein var(--t-base) var(--ease-out); }
  .t-modal { background: var(--card); border-radius: var(--r-4); box-shadow: var(--shadow-lg); width: 100%; max-width: 520px; max-height: 90vh; display: flex; flex-direction: column; animation: t-slidein var(--t-base) var(--ease-out); }
  .t-modal--sm { max-width: 380px; }
  .t-modal--wide { max-width: 720px; }
  .t-modal-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 18px; border-bottom: 1px solid var(--line); flex-shrink: 0; }
  .t-modal-head h2 { font-size: var(--fs-lg); font-weight: var(--fw-semibold); }
  .t-modal-head--rich { background: var(--card); border-radius: var(--r-4) var(--r-4) 0 0; }
  .t-modal--rich .t-modal-foot { background: var(--card); }
  .t-modal-head-main { display: flex; align-items: center; gap: 12px; min-width: 0; }
  .t-modal-head-text { min-width: 0; }
  .t-modal-head-text p { font-size: var(--fs-sm); color: var(--ink-3); margin-top: 2px; }
  .t-modal-badge { width: 40px; height: 40px; border-radius: var(--r-3); background: var(--accent-gradient); box-shadow: var(--shadow-sm); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .t-modal-body { padding: 18px; overflow-y: auto; flex: 1; }
  .t-modal-foot { padding: 14px 18px; border-top: 1px solid var(--line); display: flex; justify-content: flex-end; gap: 8px; flex-shrink: 0; background: var(--paper); border-radius: 0 0 var(--r-4) var(--r-4); }

  /* Alert */
  .t-alert { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; border-radius: var(--r-2); border: 1px solid var(--line); background: var(--neutral-soft); color: var(--ink-2); font-size: var(--fs-sm); }
  .t-alert--success { background: var(--success-soft); color: var(--success); border-color: var(--success-line); }
  .t-alert--warn { background: var(--warn-soft); color: var(--warn); border-color: var(--warn-line); }
  .t-alert--danger { background: var(--danger-soft); color: var(--danger); border-color: var(--danger-line); }
  .t-alert--info { background: var(--info-soft); color: var(--info); border-color: var(--info-line); }

  /* Toast */
  .t-toast-host { position: fixed; top: 16px; right: 16px; z-index: 9999; display: flex; flex-direction: column; gap: 8px; }
  .t-toast { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border-radius: var(--r-3); background: var(--ink); color: var(--ink-inv); font-size: var(--fs-sm); box-shadow: var(--shadow-lg); min-width: 240px; max-width: 380px; animation: t-slidein var(--t-base) var(--ease-out); }
  .t-toast--success { background: var(--success); }
  .t-toast--danger { background: var(--danger); }
  .t-toast--warn { background: var(--warn); }
  .t-toast--info { background: var(--info); }

  /* Table */
  .t-table { width: 100%; border-collapse: collapse; font-size: var(--fs-sm); }
  .t-table th { text-align: left; padding: 8px 12px; color: var(--ink-3); font-weight: var(--fw-medium); font-size: var(--fs-xs); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid var(--line); background: var(--paper); position: sticky; top: 0; z-index: 1; }
  .t-table td { padding: 9px 12px; border-bottom: 1px solid var(--line); vertical-align: middle; color: var(--ink); }
  .t-table tr:hover td { background: var(--neutral-soft); }
  .t-table tr.is-selected td { background: var(--accent-soft); }

  /* Checkbox / Radio / Switch */
  .t-check { display: inline-flex; align-items: center; gap: 7px; cursor: pointer; font-size: var(--fs-sm); color: var(--ink-2); user-select: none; }
  .t-check input[type=checkbox], .t-check input[type=radio] { width: 15px; height: 15px; accent-color: var(--accent); cursor: pointer; }
  .t-switch { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; font-size: var(--fs-sm); color: var(--ink-2); user-select: none; }
  .t-switch input { display: none; }
  .t-switch-track { width: 32px; height: 18px; border-radius: var(--r-full); background: var(--line-strong); position: relative; transition: background var(--t-fast); flex-shrink: 0; }
  .t-switch-track::after { content: ''; position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: 50%; background: white; box-shadow: var(--shadow-sm); transition: transform var(--t-base) var(--ease-out); }
  .t-switch input:checked + .t-switch-track { background: var(--accent); }
  .t-switch input:checked + .t-switch-track::after { transform: translateX(14px); }
  .t-switch.is-disabled { opacity: 0.5; cursor: not-allowed; }

  /* Context menu */
  .t-ctx-menu { position: fixed; background: var(--card); border: 1px solid var(--line); border-radius: var(--r-3); box-shadow: var(--shadow-lg); min-width: 190px; padding: 4px; z-index: 5000; animation: t-fadein var(--t-fast) var(--ease-out); }
  .t-ctx-item { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: var(--r-1); cursor: pointer; font-size: var(--fs-sm); color: var(--ink); transition: background var(--t-fast); white-space: nowrap; border: none; background: transparent; width: 100%; text-align: left; font-family: var(--font-sans); }
  .t-ctx-item:hover { background: var(--neutral-soft); }
  .t-ctx-item--danger { color: var(--danger); }
  .t-ctx-item--danger:hover { background: var(--danger-soft); }
  .t-ctx-item--disabled { opacity: 0.45; cursor: not-allowed; pointer-events: none; }
  .t-ctx-hint { margin-left: auto; font-size: var(--fs-xs); color: var(--ink-4); }
  .t-ctx-divider { height: 1px; background: var(--line); margin: 4px 0; }

  /* Empty */
  .t-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 24px; text-align: center; color: var(--ink-3); gap: 8px; }
  .t-empty-title { font-size: var(--fs-md); font-weight: var(--fw-semibold); color: var(--ink-2); }

  /* Spinner */
  .t-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid var(--line-strong); border-top-color: var(--accent); border-radius: 50%; animation: t-spin 0.7s linear infinite; flex-shrink: 0; }

  /* Skeleton */
  .t-skeleton { background: linear-gradient(90deg, var(--paper-2) 25%, var(--paper-3) 50%, var(--paper-2) 75%); background-size: 200% 100%; animation: t-shimmer 1.4s ease-in-out infinite; }

  /* Tooltip */
  .t-tip { position: relative; }
  .t-tip::after { content: attr(data-tip); position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%); background: var(--ink); color: var(--ink-inv); font-size: var(--fs-xs); padding: 4px 8px; border-radius: var(--r-1); white-space: nowrap; opacity: 0; pointer-events: none; transition: opacity var(--t-fast); z-index: 200; }
  .t-tip:hover::after { opacity: 1; }

  /* Cluster / Stack utils */
  .t-cluster { display: flex; align-items: center; gap: 8px; }
  .t-stack { display: flex; flex-direction: column; gap: 12px; }
  .t-divider { height: 1px; background: var(--line); }

  /* Kbd */
  .t-kbd { display: inline-flex; align-items: center; padding: 1px 5px; border-radius: var(--r-1); background: var(--neutral-soft); border: 1px solid var(--line-strong); font-family: var(--font-mono); font-size: var(--fs-xs); color: var(--ink-2); }

  /* Bulk bar */
  .t-bulkbar { position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: var(--ink); color: var(--ink-inv); border-radius: var(--r-4); box-shadow: var(--shadow-lg); padding: 10px 16px; display: flex; align-items: center; gap: 12px; z-index: 400; font-size: var(--fs-sm); animation: t-slidein var(--t-base) var(--ease-out); }

  /* File grid */
  .t-file-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(192px, 1fr)); gap: 12px; }
  .t-folder-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; }
  .t-file-tile { background: var(--card); border: 1.5px solid var(--line); border-radius: var(--r-3); overflow: hidden; cursor: pointer; transition: border-color var(--t-fast), box-shadow var(--t-fast); position: relative; }
  .t-file-tile:hover { border-color: var(--line-strong); box-shadow: var(--shadow); }
  .t-file-tile.is-selected { border-color: var(--accent); box-shadow: var(--shadow-focus); }
  .t-file-tile-thumb { height: 124px; background: var(--paper-2); display: flex; align-items: center; justify-content: center; overflow: hidden; position: relative; }
  .t-file-tile-foot { padding: 9px 10px; }
  .t-file-name { font-size: var(--fs-sm); font-weight: var(--fw-medium); color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .t-file-meta { font-size: var(--fs-xs); color: var(--ink-3); margin-top: 2px; }

  /* Sidebar nav */
  .t-nav-row { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: var(--r-2); cursor: pointer; font-size: var(--fs-sm); color: var(--ink-2); transition: background var(--t-fast), color var(--t-fast); border: none; background: transparent; width: 100%; text-align: left; font-family: var(--font-sans); }
  .t-nav-row:hover { background: rgba(29,30,39,0.05); color: var(--ink); }
  .t-nav-row.is-active { background: var(--accent-soft); color: var(--accent-ink); }
  .t-nav-row.is-active svg { stroke: var(--accent-ink) !important; }
  .t-nav-label { font-size: var(--fs-xs); text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-4); font-weight: var(--fw-semibold); padding: 10px 10px 4px; }
  .t-nav-badge { margin-left: auto; min-width: 18px; height: 18px; padding: 0 5px; border-radius: var(--r-full); background: var(--accent); color: white; font-size: 10px; font-weight: var(--fw-semibold); display: flex; align-items: center; justify-content: center; }

  /* Drag-over highlight */
  .t-drop-target { outline: 2px dashed var(--accent); outline-offset: -2px; background: var(--accent-soft) !important; }

  /* Progress bar */
  .t-progress { height: 6px; border-radius: var(--r-full); background: var(--paper-3); overflow: hidden; }
  .t-progress-fill { height: 100%; border-radius: var(--r-full); background: var(--accent); transition: width 0.3s var(--ease-out); }
  .t-progress-fill--danger { background: var(--danger); }
  .t-progress-fill--gradient { background: var(--accent-gradient); }

  /* Upgrade / plan banner (gradient CTA) */
  .t-upgrade-banner { position: relative; overflow: hidden; display: flex; align-items: center; gap: 10px; width: 100%; margin-top: 6px; padding: 10px 12px; border: none; border-radius: var(--r-3); background: linear-gradient(90deg, #4495F0 0%, #62A5D7 35%, #88B1BE 65%, #B8BEAB 88%, #C8C5A5 100%); color: #fff; text-align: left; cursor: pointer; box-shadow: var(--shadow-sm); transition: filter var(--t-fast), box-shadow var(--t-fast); font-family: var(--font-sans); }
  .t-upgrade-banner:hover { filter: brightness(1.06); box-shadow: var(--shadow-md); }
  .t-upgrade-banner > * { position: relative; z-index: 2; }
  /* constant glossy top sheen */
  .t-upgrade-banner::before { content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none; background: linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.08) 45%, rgba(255,255,255,0) 62%); }
  /* sweeping shine streak */
  .t-upgrade-banner::after { content: ''; position: absolute; top: 0; bottom: 0; left: -60%; width: 45%; z-index: 1; pointer-events: none; transform: skewX(-20deg); background: linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0) 100%); animation: t-shine 4.5s ease-in-out infinite; }
  @keyframes t-shine { 0% { left: -60%; } 55%, 100% { left: 130%; } }
  @media (prefers-reduced-motion: reduce) { .t-upgrade-banner::after { animation: none; } }
  .t-upgrade-pill { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; padding: 3px 12px; border-radius: var(--r-full); background: #fff; color: var(--accent-ink); font-size: var(--fs-xs); font-weight: var(--fw-semibold); box-shadow: var(--shadow-sm); }

  /* Animations */
  @keyframes t-spin { to { transform: rotate(360deg); } }
  @keyframes t-fadein { from { opacity: 0; } to { opacity: 1; } }
  @keyframes t-slidein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes t-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

  /* Dock panel */
  .t-panel { width: var(--panel-w); flex-shrink: 0; border-left: 1px solid var(--line); background: var(--card); display: flex; flex-direction: column; overflow: hidden; animation: t-slidein var(--t-base) var(--ease-out); }

  /* Viewer overlay */
  .t-viewer { position: fixed; inset: 0; background: rgba(16,24,40,0.85); backdrop-filter: blur(8px); z-index: 800; display: flex; flex-direction: column; animation: t-fadein var(--t-fast) var(--ease-out); }
  .t-viewer-bar { display: flex; align-items: center; gap: 12px; padding: 0 18px; height: var(--topbar-h); background: rgba(255,255,255,0.06); border-bottom: 1px solid rgba(255,255,255,0.1); flex-shrink: 0; }
  .t-viewer-body { flex: 1; display: flex; align-items: center; justify-content: center; overflow: auto; padding: 24px; }

  /* Drawer (side panel) */
  .t-drawer-backdrop { position: fixed; inset: 0; background: var(--overlay); z-index: 1000; display: flex; justify-content: flex-end; animation: t-fadein var(--t-base) var(--ease-out); }
  .t-drawer { width: 440px; max-width: 100%; height: 100%; background: var(--card); border-left: 1px solid var(--line); box-shadow: var(--shadow-lg); display: flex; flex-direction: column; animation: t-slidein var(--t-base) var(--ease-out); }
  .t-drawer-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 18px; border-bottom: 1px solid var(--line); flex-shrink: 0; }
  .t-drawer-head h2 { font-size: var(--fs-lg); font-weight: var(--fw-semibold); }
  .t-drawer-body { flex: 1; overflow-y: auto; padding: 18px; }
  .t-drawer-foot { padding: 14px 18px; border-top: 1px solid var(--line); display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-shrink: 0; background: var(--paper); }

  /* Filter chip toggle */
  .t-filter-chip { display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: var(--r-full); border: 1.5px solid var(--line-strong); background: var(--card); color: var(--ink-2); font-size: var(--fs-sm); font-weight: var(--fw-medium); font-family: var(--font-sans); cursor: pointer; transition: border-color var(--t-fast), background var(--t-fast), color var(--t-fast); }
  .t-filter-chip:hover { border-color: var(--ink-4); }
  .t-filter-chip.is-active { background: var(--accent-soft); border-color: var(--accent); color: var(--accent-ink); }

  /* Notification popover */
  .t-notif-popover { position: absolute; top: calc(100% + 8px); right: 0; width: 340px; background: var(--card); border: 1px solid var(--line); border-radius: var(--r-3); box-shadow: var(--shadow-lg); z-index: 300; overflow: hidden; animation: t-slidein var(--t-base) var(--ease-out); }
  .t-notif-item { display: flex; align-items: flex-start; gap: 10px; padding: 10px 14px; border-bottom: 1px solid var(--line); cursor: pointer; transition: background var(--t-fast); }
  .t-notif-item:hover { background: var(--neutral-soft); }
  .t-notif-item.is-unread { background: var(--accent-soft); }
  .t-notif-unread-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); flex-shrink: 0; margin-top: 5px; }

  /* ── Hamburger (mobile nav toggle) — hidden on desktop ── */
  .sk-hamburger { display: none; }
  .sk-nav-backdrop { display: none; }

  /* ─────────────────────────────────────────────────────────────
     Mobile / small-screen layout
     The sidebar is normally an inline 248px column. Below this
     breakpoint it becomes an off-canvas drawer toggled by the
     hamburger, and the main area takes the full viewport width.
     Inline styles win over the stylesheet, so overrides that touch
     inline-styled props use !important on purpose.
     ───────────────────────────────────────────────────────────── */
  @media (max-width: 820px) {
    .sk-sidebar {
      position: fixed !important;
      top: 0; left: 0; bottom: 0;
      z-index: 1100;
      width: 284px !important;
      max-width: 84vw;
      transform: translateX(-100%);
      transition: transform var(--t-base) var(--ease-out);
      box-shadow: var(--shadow-lg);
    }
    .sk-sidebar.is-open { transform: translateX(0); }

    .sk-nav-backdrop {
      display: block;
      position: fixed; inset: 0;
      background: var(--overlay);
      z-index: 1090;
      animation: t-fadein var(--t-base) var(--ease-out);
    }

    .sk-hamburger { display: inline-flex; }

    /* Tighter top bar; drop the fixed left padding */
    .sk-topbar { padding: 0 10px !important; gap: 8px !important; }
    /* Account name + plan text collapses to just the avatar */
    .sk-acct-text { display: none !important; }
    /* Search keeps flexing but is allowed to get small */
    .sk-search { min-width: 0; }

    /* Page chrome padding */
    .sk-page-head { padding: 14px 14px 0 !important; }
    .sk-page-toolbar { padding: 10px 14px !important; }
    .sk-page-content { padding: 14px !important; }
    /* Title takes its own row so the action buttons wrap beneath it */
    .sk-page-title { font-size: var(--fs-xl) !important; flex-basis: 100% !important; }
    /* Primary actions share the next row full-width */
    .sk-page-head .t-btn { flex: 1 1 0; }

    /* Denser card grids */
    .t-file-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 10px !important; }
    .t-folder-grid { grid-template-columns: 1fr !important; }
    .t-file-tile-thumb { height: 104px; }

    /* List view: hide low-priority columns so Name + Size stay readable */
    .sk-col-owner, .sk-col-used, .sk-col-tags { display: none; }
    .sk-filelist { overflow-x: auto; }

    /* Detail / dock panels take over the screen instead of squeezing */
    .t-panel { position: fixed !important; inset: 0 !important; width: 100% !important; z-index: 1200; }

    /* Bulk action bar hugs the screen edges */
    .t-bulkbar { left: 12px; right: 12px; transform: none; flex-wrap: wrap; justify-content: center; }

    /* Modals breathe to the edges */
    .t-modal-backdrop { padding: 12px; }
  }

  @media (max-width: 420px) {
    .t-file-grid { gap: 8px !important; }
    .sk-page-title { font-size: var(--fs-lg) !important; }
  }
`;

export const Colors = {
  accent: '#2D5CF6',
  paper: '#FAF9F5',
  card: '#FFFFFF',
  ink: '#1D1E27',
  ink2: '#5A5B68',
  ink3: '#8E8F9C',
  ink4: '#B8B9C8',
  success: '#1F8A5B',
  warn: '#B7791F',
  danger: '#C2452E',
  info: '#1C71B8',
  line: '#E4E3DB',
};

export const FileTypeColors: Record<string, string> = {
  pdf: '#E5484D',
  doc: '#2D5CF6',
  docx: '#2D5CF6',
  xls: '#1F8A5B',
  xlsx: '#1F8A5B',
  ppt: '#E08C00',
  pptx: '#E08C00',
  jpg: '#7C3AED',
  jpeg: '#7C3AED',
  png: '#7C3AED',
  gif: '#7C3AED',
  mp4: '#0E1726',
  mov: '#0E1726',
  mp3: '#9333EA',
  wav: '#9333EA',
  txt: '#64748B',
  md: '#64748B',
  zip: '#B45309',
  rar: '#B45309',
  fig: '#D6409F',
  sketch: '#D6409F',
  default: '#64748B',
};
